import {
  Divider,
  EHeadingStyleVariant,
  EIconButtonVariant,
  ELinkColorVariant,
  ELinkVariant,
  ETextStyleVariant,
  EToastVariant,
  GeneralAlertOctagonIcon,
  GeneralCopyIcon,
  GeneralInfoIcon,
  Grid,
  GridItem,
  Heading,
  IconButton,
  Link as InlineLink,
  LinkButton,
  Text,
  useIsWindowWidthGreaterThan,
} from '@outdoorsyco/bonfire';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useToast } from '@/hooks/useToast';
import { getODAnonymousId } from '@/services/segment';
import { IFullUserInfo } from '@/services/types/user/user';

import { currenciesArray } from '../ProfileEdit/Settings/currencies';
import { AddAboutMe } from './AddAboutMe/AddAboutMe';
import { CoverPhoto } from './CoverPhoto/CoverPhoto';
import { ProfilePhoto } from './ProfilePhoto/ProfilePhoto';
interface IProfileProps {
  userData: IFullUserInfo;
  isLoadingUser: boolean;
  refetchUser: () => void;
}

export const Profile = ({ userData, isLoadingUser, refetchUser }: IProfileProps) => {
  const intl = useIntl();
  const { showToast } = useToast();
  const desktopView = useIsWindowWidthGreaterThan('sm', true);

  const [anonId, setAnonId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const id = await getODAnonymousId();
      setAnonId(id);
    })();
  }, []);

  const addressInfo = [
    userData.profile.address.street,
    userData.profile.address.city,
    userData.profile.address.state,
    userData.profile.address.zip,
    userData.profile.address.country,
  ]
    .filter(Boolean)
    .join(', ');

  const currencies = currenciesArray(intl);
  const userCurrency = currencies.find(
    a =>
      a.currency === userData.profile.locale.base_currency ||
      a.name === userData.profile.locale.base_currency,
  );

  return (
    <Grid columnSpacing={2}>
      <GridItem sm={6} xs={12} className="md:pb-8">
        <div className="flex items-center justify-between mt-4 mb-6 md:mb-10">
          <Heading variant={EHeadingStyleVariant.H5}>
            <FormattedMessage defaultMessage="Personal info" id="TxlewI" />
          </Heading>
          <Link href={`/account/${userData.id}/edit`} passHref legacyBehavior>
            <LinkButton
              label={intl.formatMessage({
                defaultMessage: 'Edit',
                id: 'wEQDC6',
              })}
            />
          </Link>
        </div>
        {!desktopView && (
          <ProfilePhoto userData={userData} isLoading={isLoadingUser} refetchUser={refetchUser} />
        )}
        <div>
          <div>
            <Heading variant={EHeadingStyleVariant.H6} className="mb-3 md:mb-4">
              <FormattedMessage defaultMessage="Legal name" id="OozR1Y" />
            </Heading>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <Text variant={ETextStyleVariant.LargeRegular}>
                  <FormattedMessage defaultMessage="First name" id="pONqz8" />
                </Text>
                <Text className="overflow-hidden text-ellipsis text-secondary-base">
                  {userData.profile.first_name}
                </Text>
              </div>
              <div className="flex flex-col gap-1">
                <Text variant={ETextStyleVariant.LargeRegular}>
                  <FormattedMessage defaultMessage="Last name" id="txUL0F" />
                </Text>
                <Text className="overflow-hidden text-ellipsis text-secondary-base">
                  {userData.profile.last_name}
                </Text>
              </div>
            </div>
          </div>
          <Divider className="my-10" />
          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex flex-col mb-4 gap-1 md:mb-0">
              <Text variant={ETextStyleVariant.LargeRegular}>
                <FormattedMessage defaultMessage="Email" id="sy+pv5" />
              </Text>
              <Text className="overflow-hidden text-ellipsis text-secondary-base">
                {userData.profile.email ? userData.profile.email : ''}
              </Text>
              {(userData.pending_email || !userData.verifications.email) &&
                (userData.pending_email ? (
                  <div className="flex items-center gap-1">
                    <GeneralAlertOctagonIcon className="text-lg text-red-800" />
                    <Text className="text-sm text-secondary-base">
                      <FormattedMessage defaultMessage="Email not verified" id="8LUqHf" />
                    </Text>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <GeneralInfoIcon className="text-lg text-yellow-600" />
                    <Text className="text-sm text-secondary-base">
                      <FormattedMessage defaultMessage="Email change pending" id="ifvUWP" />
                    </Text>
                  </div>
                ))}
            </div>
            <div className="flex flex-col gap-1">
              <Text variant={ETextStyleVariant.LargeRegular}>
                <FormattedMessage defaultMessage="Phone number" id="jdJhOL" />
              </Text>
              <Text className="overflow-hidden text-ellipsis text-secondary-base">
                {userData.profile.phone ? userData.profile.phone : ''}
              </Text>
              {!userData.verifications.phone && (
                <div className="flex items-center gap-1">
                  <GeneralAlertOctagonIcon className="text-lg text-red-800" />
                  <Text className="text-sm text-secondary-base">
                    <FormattedMessage defaultMessage="Phone number not verified" id="CAELOD" />
                  </Text>
                </div>
              )}
            </div>
          </div>
          <Divider className="my-10" />
          <div>
            <Heading variant={EHeadingStyleVariant.H6} className="mb-4">
              <FormattedMessage defaultMessage="About me" id="1I78TO" />
            </Heading>
            {userData.profile.business.description ? (
              <Text className="text-secondary-base">{userData.profile.business.description}</Text>
            ) : (
              <AddAboutMe userId={userData.id} />
            )}
          </div>
          {!desktopView && (
            <>
              <Divider className="my-10" />
              <CoverPhoto userData={userData} refetchUser={refetchUser} isLoading={isLoadingUser} />
            </>
          )}
          <Divider className="my-10" />
          <div>
            <Heading variant={EHeadingStyleVariant.H6} className="mb-4">
              <FormattedMessage defaultMessage="Address" id="e6Ph5+" />
            </Heading>
            <InlineLink
              variant={ELinkVariant.Underline}
              colorVariant={ELinkColorVariant.Secondary}
              textVariant={ETextStyleVariant.MediumRegular}
              target="_blank"
              href={`https://www.google.com/maps/search/${encodeURI(addressInfo)}`}
              className="text-secondary-base">
              {addressInfo}
            </InlineLink>
          </div>
          {desktopView && (
            <>
              <Divider className="my-10" />
              <CoverPhoto userData={userData} refetchUser={refetchUser} isLoading={isLoadingUser} />
            </>
          )}
          <Divider className="my-10" />
          <div className="flex flex-col gap-10">
            <Heading variant={EHeadingStyleVariant.H6}>
              <FormattedMessage defaultMessage="Settings" id="D3idYv" />
            </Heading>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <Text variant={ETextStyleVariant.LargeRegular}>
                  <FormattedMessage defaultMessage="Time zone" id="IcUakl" />
                </Text>
                <Text className="overflow-hidden text-ellipsis text-secondary-base">
                  {userData.profile.locale.timezone}
                </Text>
              </div>
              <div className="flex flex-col gap-1">
                <Text variant={ETextStyleVariant.LargeRegular}>
                  <FormattedMessage defaultMessage="Default currency" id="nGY+Zu" />
                </Text>
                <Text className="overflow-hidden text-ellipsis text-secondary-base">
                  {userCurrency?.translatedName || ''}
                </Text>
              </div>
            </div>
            <Link href={`/account/${userData?.id}/edit?zone=Password`} passHref legacyBehavior>
              <LinkButton
                label={intl.formatMessage({
                  defaultMessage: 'Change password',
                  id: 'L4nXIc',
                })}
              />
            </Link>
          </div>
          {anonId && (
            <>
              <Divider className="my-10" />
              <div className="mb-8">
                <Heading variant={EHeadingStyleVariant.H6} className="mb-4">
                  <FormattedMessage defaultMessage="Anonymous ID" id="qgfZvk" />
                </Heading>
                <div className="flex items-center gap-1">
                  <Text className="text-secondary-base">{anonId}</Text>
                  <IconButton
                    tooltip={intl.formatMessage({
                      defaultMessage: 'Copy',
                      id: '4l6vz1',
                    })}
                    icon={GeneralCopyIcon}
                    variant={EIconButtonVariant.GhostSquareSmall}
                    onClick={() => {
                      navigator.clipboard.writeText(anonId);
                      showToast(
                        EToastVariant.Success,
                        intl.formatMessage({ defaultMessage: 'Success!', id: 'UIDXdt' }),
                        intl.formatMessage({
                          defaultMessage: 'Anonymous ID copied to clipboard',
                          id: '3DLtp4',
                        }),
                      );
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </GridItem>
      <GridItem sm={1} xs={1} />
      {desktopView && (
        <GridItem sm={5} xs={12}>
          <ProfilePhoto userData={userData} isLoading={isLoadingUser} refetchUser={refetchUser} />
        </GridItem>
      )}
    </Grid>
  );
};
